import * as React from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { getViewMode } from '@/lib/utils'
import { Search, Loader2, X, TrendingUp } from 'lucide-react'
import { motion } from 'framer-motion'

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('q') || '')
  const [inputValue, setInputValue] = React.useState(searchParams.get('q') || '')
  const [viewMode, setViewMode] = React.useState(getViewMode())
  const { ref, inView } = useInView()

  // Popular searches (could be fetched from API)
  const popularSearches = [
    'Politics', 'Technology', 'Business', 'Sports', 'Entertainment',
    'Science', 'Health', 'Climate', 'Education', 'World News'
  ]

  // Recent searches from localStorage
  const [recentSearches, setRecentSearches] = React.useState(() => {
    try {
      const saved = localStorage.getItem('recentSearches')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  // Search query
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['search', searchQuery],
    queryFn: ({ pageParam = 1 }) => {
      if (!searchQuery) return Promise.resolve({ data: { results: [], next: null } })
      return newsApi.search(searchQuery, pageParam)
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.data?.next) return undefined
      const url = new URL(lastPage.data.next)
      return url.searchParams.get('page')
    },
    enabled: !!searchQuery,
  })

  // Load more when scrolling
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Handle search
  const handleSearch = (query) => {
    if (!query.trim()) return
    
    setSearchQuery(query)
    setSearchParams({ q: query })
    
    // Save to recent searches
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5)
    setRecentSearches(updated)
    localStorage.setItem('recentSearches', JSON.stringify(updated))
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setInputValue('')
    setSearchParams({})
  }

  const handleClearRecent = () => {
    setRecentSearches([])
    localStorage.removeItem('recentSearches')
  }

  const articles = data?.pages.flatMap(page => page.data.results || []) || []
  const totalResults = data?.pages[0]?.data?.count || 0

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Search News
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Search through thousands of articles from trusted sources
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(inputValue)
                }
              }}
              placeholder="Search articles, topics, sources..."
              className="w-full pl-12 pr-24 py-4 text-lg border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
            />
            {inputValue && (
              <button
                onClick={() => setInputValue('')}
                className="absolute right-20 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <Button
              onClick={() => handleSearch(inputValue)}
              disabled={!inputValue.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            >
              Search
            </Button>
          </div>
        </motion.div>

        {/* Results or Suggestions */}
        {!searchQuery ? (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Recent Searches
                  </h2>
                  <button
                    onClick={handleClearRecent}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    Clear
                  </button>
                </div>
                <div className="space-y-2">
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setInputValue(search)
                        handleSearch(search)
                      }}
                      className="w-full text-left px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors flex items-center gap-2"
                    >
                      <Search className="h-4 w-4 text-gray-400" />
                      {search}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Popular Searches */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Popular Topics
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {popularSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setInputValue(search)
                      handleSearch(search)
                    }}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-primary/10 text-gray-700 dark:text-gray-300 hover:text-primary rounded-full text-sm font-medium transition-colors"
                  >
                    {search}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        ) : (
          <>
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Search Results for "{searchQuery}"
                </h2>
                {!isLoading && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {totalResults} {totalResults === 1 ? 'result' : 'results'} found
                  </p>
                )}
              </div>
              <Button variant="outline" onClick={handleClearSearch}>
                Clear Search
              </Button>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <NewsCardSkeleton key={i} variant={viewMode} />
                ))}
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="text-center py-12">
                <div className="bg-red-50 dark:bg-red-900/20 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                  <X className="w-12 h-12 text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Search Failed
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Something went wrong. Please try again.
                </p>
                <Button onClick={() => handleSearch(searchQuery)}>
                  Retry Search
                </Button>
              </div>
            )}

            {/* No Results */}
            {!isLoading && !isError && articles.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No Results Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Try different keywords or browse popular topics
                </p>
                <Button onClick={handleClearSearch}>
                  Clear Search
                </Button>
              </div>
            )}

            {/* Results */}
            {!isLoading && !isError && articles.length > 0 && (
              <div className="space-y-4">
                {articles.map((article) => (
                  <NewsCard
                    key={article.alias}
                    article={article}
                    variant={viewMode}
                  />
                ))}

                {/* Load More Trigger */}
                <div ref={ref} className="py-4">
                  {isFetchingNextPage && (
                    <div className="flex justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  )}
                </div>

                {/* End of Results */}
                {!hasNextPage && articles.length > 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    You've reached the end of the results
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
